/**
 * Scope-aware allowlist for analytical variable-slug auto-linking.
 *
 * The slug regex (`severe_asthma_index_date` etc.) is deliberately loose —
 * 3+ underscore-separated lowercase words. That pattern also matches plenty
 * of everyday SQL identifiers (`value_as_number`, `concept_group_id`,
 * `unit_source_value`) and CTE names (`direct_bmi_raw`, `qualifying_events`),
 * so pattern-only matching produces dozens of false positives inside a
 * generated SQL block. The fix: when we know the universe of valid analytical variable slugs
 * for the current scope (the open chat session's artifacts), pass it down
 * via this context and gate auto-linking on membership.
 *
 * Semantics:
 *   - `null` (default) → pattern-only fallback, original behavior. Used by
 *     surfaces that don't have a concrete slug universe (e.g. the knowledge
 *     browser, where snippets can reference any analytical variable in the corpus).
 *   - `Set<string>`  → only slugs in the set are linkable. An empty set
 *     means "I know there are no valid slugs here, link nothing."
 */

import { createContext, useContext, type ReactNode } from 'react';

type KnownAvSlugs = ReadonlySet<string> | null;

const KnownAvSlugsContext = createContext<KnownAvSlugs>(null);

interface KnownAvSlugsProviderProps {
  slugs: KnownAvSlugs;
  children: ReactNode;
}

export const KnownAvSlugsProvider = ({ slugs, children }: KnownAvSlugsProviderProps) => (
  <KnownAvSlugsContext.Provider value={slugs}>{children}</KnownAvSlugsContext.Provider>
);

export const useKnownAvSlugs = (): KnownAvSlugs => useContext(KnownAvSlugsContext);
